import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Button,
  Typography,
  CircularProgress,
  Alert,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  MenuItem,
  InputAdornment,
  IconButton,
  Collapse,
  Pagination,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { format as formatDateFns, parse as parseDateFns, isValid } from "date-fns";
import { FiCheckCircle, FiSearch, FiFilter, FiX } from "react-icons/fi";

import { fetchIwoStatus, updateIwoStatus } from "../actions/IwoStatusAction";

// I = In Progress, C = Send to Production, E = Production Complete
const IWO_STATUS_LABELS = {
  I: "In Progress",
  C: "Send to Production",
  E: "Production Complete",
};

// one colour per status code
const IWO_STATUS_COLORS = {
  I: "#ED6C02", // orange  — in progress
  C: "#BF360C", // dark orange — sent to production
  E: "#2E7D32", // green   — completed
};

const getStatusLabel = (code) => IWO_STATUS_LABELS[code] || code || "-";
const getStatusColor = (code) => IWO_STATUS_COLORS[code] || "#154C79";

// I (In Progress) and C (Send to Production) can both be moved to E
const canCompleteStatus = (code) => code === "I" || code === "C";

// always render dates as yyyy-mm-dd
const toYMD = (value) => {
  if (!value) return "-";

  const text = String(value).trim();

  // already yyyy-mm-dd (or yyyy-mm-dd hh:mm:ss)
  const match = text.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (match) return `${match[1]}-${match[2]}-${match[3]}`;

  const date = new Date(text);
  if (isNaN(date.getTime())) return text;

  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");

  return `${yyyy}-${mm}-${dd}`;
};

// yyyy-mm-dd string <-> Date, for the pickers
const parseYMD = (value) => {
  if (!value) return null;

  const date = parseDateFns(value, "yyyy-MM-dd", new Date());
  return isValid(date) ? date : null;
};

const formatYMD = (date) =>
  date && isValid(date) ? formatDateFns(date, "yyyy-MM-dd") : "";

// records per page
const PAGE_SIZE = 10;

const inputStyle = {
  backgroundColor: "#F0F4F8",
  borderRadius: "10px",
  "& .MuiOutlinedInput-root": {
    borderRadius: "10px",
    "& fieldset": { borderColor: "#154C79" },
    "&:hover fieldset": { borderColor: "#154C79" },
    "&.Mui-focused fieldset": { borderColor: "#154C79" },
  },
  "& .MuiInputLabel-root": { color: "#154C79" },
  "& .MuiInputLabel-root.Mui-focused": { color: "#154C79" },
};


const IwoStatusManage = ({ serviceNo }) => {
  const dispatch = useDispatch();

  const {
    // data: iwoStatusData = [],
    data: rawIwoStatusData = [],
    loading,
    error,
    updating,
    updatingIwoNo,
  } = useSelector((state) => state.iwoStatus || {});

  // hide production-complete (E) IWOs that already have WD_Received_By filled
  const iwoStatusData = useMemo(
    () =>
      rawIwoStatusData.filter((item) => {
        const wdReceivedBy = String(item.WD_Received_By ?? "").trim();
        return !(item.IwoStatusCode === "E" && wdReceivedBy !== "");
      }),
    [rawIwoStatusData]
  );

  const [confirmItem, setConfirmItem] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  // search + filters
  const [searchText, setSearchText] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [startDateFrom, setStartDateFrom] = useState("");
  const [endDateTo, setEndDateTo] = useState("");

  // paging
  const [page, setPage] = useState(1);

  useEffect(() => {
    if (serviceNo) {
      dispatch(fetchIwoStatus(serviceNo));
    }
  }, [dispatch, serviceNo]);

  const filteredData = useMemo(() => {
    const text = searchText.trim().toLowerCase();

    const rows = iwoStatusData.filter((item) => {
      // search — IWO No or Eng Spec
      if (text) {
        const iwoNo = String(item.IwoNo || "").toLowerCase();
        const engSpec = String(item.EngSpec || "").toLowerCase();

        if (!iwoNo.includes(text) && !engSpec.includes(text)) return false;
      }

      // status
      if (statusFilter !== "ALL" && item.IwoStatusCode !== statusFilter) {
        return false;
      }

      // dates are yyyy-mm-dd strings, so plain string compare works
      if (startDateFrom && (!item.StartDate || item.StartDate < startDateFrom)) {
        return false;
      }

      if (endDateTo && (!item.EndDate || item.EndDate > endDateTo)) {
        return false;
      }

      return true;
    });

    // newest IWO first
    return rows.sort((a, b) => {
      const numA = Number(a.IwoNo);
      const numB = Number(b.IwoNo);

      if (!isNaN(numA) && !isNaN(numB)) return numB - numA;

      return String(b.IwoNo || "").localeCompare(String(a.IwoNo || ""));
    });
  }, [iwoStatusData, searchText, statusFilter, startDateFrom, endDateTo]);

  const pageCount = Math.ceil(filteredData.length / PAGE_SIZE) || 1;

  // keep the page in range when the result set shrinks
  useEffect(() => {
    setPage((prev) => (prev > pageCount ? pageCount : prev));
  }, [pageCount]);

  // back to page 1 whenever the search / filters change
  useEffect(() => {
    setPage(1);
  }, [searchText, statusFilter, startDateFrom, endDateTo]);

  const pageStart = (page - 1) * PAGE_SIZE;
  const visibleData = filteredData.slice(pageStart, pageStart + PAGE_SIZE);

  const hasFilters =
    Boolean(searchText) ||
    statusFilter !== "ALL" ||
    Boolean(startDateFrom) ||
    Boolean(endDateTo);

  const handleClearFilters = () => {
    setSearchText("");
    setStatusFilter("ALL");
    setStartDateFrom("");
    setEndDateTo("");
  };

  const handlePageChange = (event, value) => {
    setPage(value);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleConfirmUpdate = async () => {
    if (!confirmItem) return;

    const iwoNo = confirmItem.IwoNo;
    setConfirmItem(null);

    // the moment the user pressed Complete -> PID_IWO_COMPLETED_DATE
    const completedDate = formatDateFns(new Date(), "yyyy-MM-dd HH:mm:ss");

    // only I / C -> E is allowed; serviceNo -> PID_IWO_COMPLETED_BY
    const res = await dispatch(
      updateIwoStatus(iwoNo, "E", serviceNo, completedDate)
    );

    setSnackbarMessage(
      res?.success
        ? `IWO ${iwoNo} marked as Production Complete`
        : res?.message || "Failed to update IWO status"
    );
    setSnackbarSeverity(res?.success ? "success" : "error");
    setSnackbarOpen(true);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
    <Box sx={{ maxWidth: 500, mx: "auto", mt: 2, px: 1, pb: 4 }}>
      {/* Search */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
        <TextField
          fullWidth
          size="small"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          placeholder="Search IWO No or Eng Spec..."
          sx={inputStyle}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <FiSearch color="#154C79" />
              </InputAdornment>
            ),
            endAdornment: searchText ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearchText("")}>
                  <FiX color="#154C79" />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
        />

        <IconButton
          onClick={() => setShowFilters((prev) => !prev)}
          sx={{
            backgroundColor: showFilters || hasFilters ? "#142C54" : "#154C79",
            color: "#fff",
            borderRadius: "10px",
            "&:hover": { backgroundColor: "#0E3C59" },
          }}
        >
          <FiFilter />
        </IconButton>
      </Box>

      {/* Filters */}
      <Collapse in={showFilters}>
        <Box
          sx={{
            p: 2,
            mb: 2,
            borderRadius: "10px",
            backgroundColor: "#F0F4F8",
            boxShadow: "0px 4px 8px rgba(0,0,0,0.08)",
          }}
        >
          <TextField
            select
            fullWidth
            size="small"
            label="Status"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            sx={{ ...inputStyle, mb: 2, backgroundColor: "#fff" }}
          >
            <MenuItem value="ALL">All</MenuItem>
            <MenuItem value="C">Send to Production</MenuItem>
            <MenuItem value="I">In Progress</MenuItem>
            <MenuItem value="E">Production Complete</MenuItem>
            
          </TextField>

          <DatePicker
            label="Start Date From"
            format="yyyy-MM-dd"
            value={parseYMD(startDateFrom)}
            onChange={(date) => setStartDateFrom(formatYMD(date))}
            slotProps={{
              field: { clearable: true },
              textField: {
                fullWidth: true,
                size: "small",
                placeholder: "yyyy-mm-dd",
                sx: { ...inputStyle, mb: 2, backgroundColor: "#fff" },
              },
            }}
          />

          <DatePicker
            label="End Date To"
            format="yyyy-MM-dd"
            value={parseYMD(endDateTo)}
            onChange={(date) => setEndDateTo(formatYMD(date))}
            slotProps={{
              field: { clearable: true },
              textField: {
                fullWidth: true,
                size: "small",
                placeholder: "yyyy-mm-dd",
                sx: { ...inputStyle, backgroundColor: "#fff" },
              },
            }}
          />

          {hasFilters && (
            <Button
              fullWidth
              onClick={handleClearFilters}
              sx={{ mt: 1, color: "#154C79", fontWeight: "bold" }}
            >
              Clear Filters
            </Button>
          )}
        </Box>
      </Collapse>

      {loading && (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
          <CircularProgress />
        </Box>
      )}

      {!loading && error && (
        <Alert severity="error" sx={{ borderRadius: "10px" }}>
          {error}
        </Alert>
      )}

      {/* Result count */}
      {!loading && !error && filteredData.length > 0 && (
        <Typography
          sx={{ fontSize: "0.8rem", color: "#154C79", mb: 1, ml: 0.5 }}
        >
          {pageStart + 1}&ndash;{pageStart + visibleData.length} of{" "}
          {filteredData.length}
          {filteredData.length !== iwoStatusData.length &&
            ` (${iwoStatusData.length} total)`}
          &nbsp;&middot;&nbsp; Page {page} of {pageCount}
        </Typography>
      )}

      {!loading && !error && iwoStatusData.length === 0 && (
        <Typography align="center" sx={{ color: "#154C79", mt: 2 }}>
          No IWO records found
        </Typography>
      )}

      {!loading &&
        !error &&
        iwoStatusData.length > 0 &&
        filteredData.length === 0 && (
          <Typography align="center" sx={{ color: "#154C79", mt: 2 }}>
            No IWO matches your search / filters
          </Typography>
        )}

      {!loading &&
        visibleData.map((item, index) => {
          const canComplete = canCompleteStatus(item.IwoStatusCode);
          const isRowUpdating = updating && updatingIwoNo === item.IwoNo;

          return (
            <Box
              key={`${item.IwoNo}-${index}`}
              sx={{
                mb: 2,
                p: 2,
                borderRadius: "10px",
                boxShadow: "0px 4px 8px rgba(0,0,0,0.1)",
                backgroundColor: "#F0F4F8",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Typography sx={{ fontWeight: "bold", color: "#142C54" }}>
                  IWO No: {item.IwoNo}
                </Typography>

                <Typography
                  sx={{
                    fontSize: "0.75rem",
                    fontWeight: "bold",
                    color: "#fff",
                    backgroundColor: getStatusColor(item.IwoStatusCode),
                    borderRadius: "12px",
                    px: 1.5,
                    py: 0.3,
                  }}
                >
                  {getStatusLabel(item.IwoStatusCode)}
                </Typography>
              </Box>

              <Typography sx={{ fontSize: "0.85rem", color: "#154C79" }}>
                EWO No: {item.EwoNo}
              </Typography>

              <Typography sx={{ fontSize: "0.85rem", color: "#142C54", mt: 1 }}>
                {item.EngSpec}
              </Typography>

              <Typography sx={{ fontSize: "0.8rem", color: "#154C79", mt: 1 }}>
                Est. Labour Hours: {item.EstimatedLabHours}
              </Typography>

              <Typography sx={{ fontSize: "0.8rem", color: "#154C79" }}>
                {toYMD(item.StartDate)} &nbsp;&ndash;&nbsp; {toYMD(item.EndDate)}
              </Typography>

              {/* Status update — only In Progress can be moved to Production Complete */}
              {canComplete ? (
                <Button
                  variant="contained"
                  fullWidth
                  disabled={updating}
                  onClick={() => setConfirmItem(item)}
                  sx={{
                    mt: 2,
                    backgroundColor: "#142C54",
                    color: "#fff",
                    borderRadius: "10px",
                    padding: "8px",
                    "&:hover": { backgroundColor: "#0E3C59" },
                  }}
                >
                  {isRowUpdating ? (
                    <CircularProgress size={20} sx={{ color: "#fff" }} />
                  ) : (
                    <>
                      <FiCheckCircle style={{ marginRight: 8 }} />
                      Mark as Production Complete
                    </>
                  )}
                </Button>
              ) : (
                <Typography
                  sx={{
                    mt: 2,
                    fontSize: "0.8rem",
                    fontStyle: "italic",
                    color: getStatusColor(item.IwoStatusCode),
                  }}
                >
                  Production completed — no further update allowed
                </Typography>
              )}
            </Box>
          );
        })}

      {/* Pagination */}
      {!loading && !error && pageCount > 1 && (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
          <Pagination
            count={pageCount}
            page={page}
            onChange={handlePageChange}
            siblingCount={0}
            boundaryCount={1}
            size="small"
            shape="rounded"
            sx={{
              "& .MuiPaginationItem-root": {
                color: "#154C79",
                fontWeight: "bold",
              },
              "& .Mui-selected": {
                backgroundColor: "#142C54 !important",
                color: "#fff !important",
              },
            }}
          />
        </Box>
      )}

      {/* Confirm dialog */}
      <Dialog open={Boolean(confirmItem)} onClose={() => setConfirmItem(null)}>
        <DialogTitle sx={{ color: "#142C54", fontWeight: "bold" }}>
          Update IWO Status
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Change IWO No <b>{confirmItem?.IwoNo}</b> from{" "}
            <b>{getStatusLabel(confirmItem?.IwoStatusCode)}</b> to{" "}
            <b>Production Complete</b>?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmItem(null)} sx={{ color: "#154C79" }}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmUpdate}
            variant="contained"
            sx={{
              backgroundColor: "#142C54",
              "&:hover": { backgroundColor: "#0E3C59" },
            }}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
    </LocalizationProvider>
  );
};

export default IwoStatusManage;